import type {
  DirectoryEvent,
  RobotDirectoryClient,
  UserDirectoryEntry,
  UserFlowSignal,
} from './RobotDirectoryClient';
import {
  createSharedFlowSnapshot,
  parseSharedFlowSnapshot,
  type SharedFlowSnapshot,
} from '@/flow/realtimeFlow';
import type { AppEdge, AppNode } from '@/flow/types';

export type UserFlowConnectionState = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'failed';

export type UserFlowManagerSnapshot = {
  users: UserDirectoryEntry[];
  selectedConnectionId: string | null;
  selectedUser: UserDirectoryEntry | null;
  connectionState: UserFlowConnectionState;
  remoteFlow: SharedFlowSnapshot | null;
  lastError: string | null;
};

type Listener = () => void;
type PeerRole = 'publisher' | 'viewer';
type ChunkTransfer = { total: number; chunks: string[] };
type EarlyIceCandidates = { sourceConnectionId: string; candidates: RTCIceCandidateInit[] };
type PeerSession = {
  sessionId: string;
  targetConnectionId: string;
  role: PeerRole;
  peer: RTCPeerConnection;
  channel: RTCDataChannel | null;
  pendingIce: RTCIceCandidateInit[];
  transfers: Map<string, ChunkTransfer>;
  fallbackActive: boolean;
};

type DataMessage =
  | { type: 'requestSnapshot' }
  | { type: 'snapshotChunk'; transferId: string; index: number; total: number; payload: string };

const CHUNK_SIZE = 6_000;
const MAX_CHUNKS = 384;
const MAX_EARLY_ICE_SESSIONS = 32;
const MAX_EARLY_ICE_CANDIDATES = 64;

export class UserFlowConnectionManager {
  private users: UserDirectoryEntry[] = [];
  private selectedConnectionId: string | null = null;
  private selectedUser: UserDirectoryEntry | null = null;
  private connectionState: UserFlowConnectionState = 'idle';
  private remoteFlow: SharedFlowSnapshot | null = null;
  private lastError: string | null = null;
  private localFlow: SharedFlowSnapshot | null = null;
  private revision = 0;
  private listeners = new Set<Listener>();
  private sessions = new Map<string, PeerSession>();
  private earlyIce = new Map<string, EarlyIceCandidates>();
  private unsubscribeDirectory: () => void;
  private selectionGeneration = 0;

  constructor(
    private readonly directory: Pick<RobotDirectoryClient, 'subscribe' | 'sendUserFlowSignal' | 'setUserFlowViewing'>,
    private readonly iceServerProvider: () => Promise<RTCIceServer[]>,
  ) {
    this.unsubscribeDirectory = directory.subscribe((event) => this.handleDirectoryEvent(event));
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  getSnapshot(): UserFlowManagerSnapshot {
    return {
      users: this.visibleUsers(),
      selectedConnectionId: this.selectedConnectionId,
      selectedUser: this.selectedUser,
      connectionState: this.connectionState,
      remoteFlow: this.remoteFlow,
      lastError: this.lastError,
    };
  }

  private visibleUsers(): UserDirectoryEntry[] {
    const byUser = new Map<string, UserDirectoryEntry>();
    for (const user of this.users) {
      const current = byUser.get(user.userId);
      if (!current || current.connectedAt < user.connectedAt) byUser.set(user.userId, user);
    }
    return [...byUser.values()];
  }

  publish(nodes: AppNode[], edges: AppEdge[]) {
    this.revision += 1;
    this.localFlow = createSharedFlowSnapshot(this.revision, nodes, edges);
    for (const session of this.sessions.values()) {
      if (
        session.role === 'publisher'
        && (session.fallbackActive || session.channel?.readyState === 'open')
      ) {
        this.sendSnapshot(session);
      }
    }
  }

  async selectUser(connectionId: string | null): Promise<void> {
    const generation = ++this.selectionGeneration;
    this.directory.setUserFlowViewing(null);
    this.closeViewerSession(true);
    this.selectedConnectionId = connectionId;
    this.selectedUser = connectionId
      ? this.users.find((user) => user.connectionId === connectionId) ?? null
      : null;
    this.remoteFlow = null;
    this.lastError = null;
    if (!connectionId) {
      this.connectionState = 'idle';
      this.emit();
      return;
    }
    if (!this.users.some((user) => user.connectionId === connectionId)) {
      this.connectionState = 'failed';
      this.lastError = '選択したユーザーは接続できません';
      this.emit();
      return;
    }
    this.connectionState = 'connecting';
    if (!this.directory.setUserFlowViewing(connectionId)) {
      this.failViewer(new Error('閲覧人数を更新できません'));
      return;
    }
    this.emit();
    try {
      const iceServers = await this.iceServerProvider();
      if (generation !== this.selectionGeneration || this.selectedConnectionId !== connectionId) return;
      const session = this.createSession('viewer', connectionId, crypto.randomUUID(), iceServers);
      const channel = session.peer.createDataChannel('filo-user-flow', { ordered: true });
      this.attachChannel(session, channel);
      const offer = await session.peer.createOffer();
      await session.peer.setLocalDescription(offer);
      this.sendSignal(session, { type: 'offer', sdp: offer.sdp ?? '' });
    } catch (error) {
      this.failViewer(error);
    }
  }

  dispose() {
    this.directory.setUserFlowViewing(null);
    this.unsubscribeDirectory();
    for (const session of this.sessions.values()) this.closeSession(session, false);
    this.sessions.clear();
    this.earlyIce.clear();
    this.listeners.clear();
  }

  private handleDirectoryEvent(event: DirectoryEvent) {
    if (event.type === 'userDirectorySnapshot' && Array.isArray(event.users)) {
      this.users = event.users;
      this.ensureSelectionAvailable();
      this.emit();
      return;
    }
    if (event.type === 'userStatus' && event.user && typeof event.online === 'boolean') {
      this.users = event.online
        ? [...this.users.filter((user) => user.connectionId !== event.user!.connectionId), event.user]
        : this.users.filter((user) => user.connectionId !== event.user!.connectionId);
      this.ensureSelectionAvailable();
      this.emit();
      return;
    }
    if (event.type === 'userViewerCounts' && Array.isArray(event.counts)) {
      const counts = new Map(event.counts.map(({ connectionId, viewerCount }) => [connectionId, viewerCount]));
      this.users = this.users.map((user) => ({
        ...user,
        viewerCount: counts.get(user.connectionId) ?? 0,
      }));
      this.ensureSelectionAvailable();
      this.emit();
      return;
    }
    if (
      event.type === 'userFlowSignal'
      && event.sourceConnectionId
      && event.sessionId
      && event.signal
    ) {
      const { sourceConnectionId, sessionId, signal } = event;
      void this.handleSignal(sourceConnectionId, sessionId, signal).catch((error) => {
        if (this.sessions.get(sessionId)?.role === 'viewer') this.failViewer(error);
      });
    }
  }

  private ensureSelectionAvailable() {
    if (!this.selectedConnectionId) return;
    const selected = this.users.find((user) => user.connectionId === this.selectedConnectionId);
    if (selected) {
      this.selectedUser = selected;
      return;
    }
    this.directory.setUserFlowViewing(null);
    this.closeViewerSession(false);
    this.connectionState = 'disconnected';
    this.lastError = '選択したユーザーが切断されました';
  }

  private async handleSignal(sourceConnectionId: string, sessionId: string, signal: UserFlowSignal) {
    if (signal.type === 'close') {
      this.earlyIce.delete(sessionId);
      const closed = this.sessions.get(sessionId);
      if (closed) this.closeSession(closed, false);
      return;
    }
    let session = this.sessions.get(sessionId);
    if (signal.type === 'requestSnapshot' || signal.type === 'snapshotChunk') {
      if (!session || session.targetConnectionId !== sourceConnectionId) return;
      session.fallbackActive = true;
      if (signal.type === 'requestSnapshot') {
        if (session.role === 'publisher') this.sendSnapshot(session);
      } else if (session.role === 'viewer') {
        this.handleDataMessageValue(session, signal);
      }
      return;
    }
    if (signal.type === 'offer') {
      if (session) this.closeSession(session, false);
      const iceServers = await this.iceServerProvider();
      session = this.createSession('publisher', sourceConnectionId, sessionId, iceServers);
      const earlyIce = this.earlyIce.get(sessionId);
      this.earlyIce.delete(sessionId);
      if (earlyIce?.sourceConnectionId === sourceConnectionId) {
        session.pendingIce.push(...earlyIce.candidates);
      }
      session.peer.ondatachannel = (event) => this.attachChannel(session!, event.channel);
      await session.peer.setRemoteDescription({ type: 'offer', sdp: signal.sdp });
      await this.flushPendingIce(session);
      const answer = await session.peer.createAnswer();
      await session.peer.setLocalDescription(answer);
      this.sendSignal(session, { type: 'answer', sdp: answer.sdp ?? '' });
      return;
    }
    if (signal.type === 'answer') {
      if (!session) return;
      await session.peer.setRemoteDescription({ type: 'answer', sdp: signal.sdp });
      await this.flushPendingIce(session);
      return;
    }
    const candidate: RTCIceCandidateInit = {
      candidate: signal.candidate,
      sdpMid: signal.sdpMid,
      sdpMLineIndex: signal.sdpMLineIndex,
      usernameFragment: signal.usernameFragment,
    };
    if (!session) {
      this.retainEarlyIce(sourceConnectionId, sessionId, candidate);
      return;
    }
    if (!session.peer.remoteDescription) session.pendingIce.push(candidate);
    else await session.peer.addIceCandidate(candidate);
  }

  private retainEarlyIce(
    sourceConnectionId: string,
    sessionId: string,
    candidate: RTCIceCandidateInit,
  ) {
    const current = this.earlyIce.get(sessionId);
    if (current && current.sourceConnectionId !== sourceConnectionId) return;
    if (!current && this.earlyIce.size >= MAX_EARLY_ICE_SESSIONS) {
      const oldestSessionId = this.earlyIce.keys().next().value as string | undefined;
      if (oldestSessionId) this.earlyIce.delete(oldestSessionId);
    }
    const retained = current ?? { sourceConnectionId, candidates: [] };
    if (retained.candidates.length >= MAX_EARLY_ICE_CANDIDATES) return;
    retained.candidates.push(candidate);
    this.earlyIce.set(sessionId, retained);
  }

  private createSession(
    role: PeerRole,
    targetConnectionId: string,
    sessionId: string,
    iceServers: RTCIceServer[],
  ): PeerSession {
    const peer = new RTCPeerConnection({ iceServers });
    const session: PeerSession = {
      sessionId,
      targetConnectionId,
      role,
      peer,
      channel: null,
      pendingIce: [],
      transfers: new Map(),
      fallbackActive: false,
    };
    this.sessions.set(sessionId, session);
    peer.onicecandidate = (event) => {
      if (!event.candidate) return;
      const candidate = event.candidate.toJSON();
      this.sendSignal(session, {
        type: 'iceCandidate',
        candidate: candidate.candidate ?? '',
        sdpMid: candidate.sdpMid,
        sdpMLineIndex: candidate.sdpMLineIndex,
        usernameFragment: candidate.usernameFragment,
      });
    };
    peer.onconnectionstatechange = () => {
      if (role !== 'viewer' || this.selectedConnectionId !== targetConnectionId) return;
      if (peer.connectionState === 'failed' || peer.connectionState === 'closed') {
        this.activateFallback(session);
      } else if (peer.connectionState === 'disconnected') {
        this.connectionState = 'disconnected';
        this.emit();
      }
    };
    return session;
  }

  private attachChannel(session: PeerSession, channel: RTCDataChannel) {
    session.channel = channel;
    channel.onopen = () => {
      if (session.role === 'viewer') {
        this.connectionState = 'connected';
        this.lastError = null;
        channel.send(JSON.stringify({ type: 'requestSnapshot' } satisfies DataMessage));
        this.emit();
      }
    };
    channel.onmessage = (event) => this.handleDataMessage(session, String(event.data));
    channel.onclose = () => {
      if (session.role === 'viewer' && this.sessions.has(session.sessionId)) {
        this.connectionState = 'disconnected';
        this.emit();
      }
    };
  }

  private handleDataMessage(session: PeerSession, raw: string) {
    let message: DataMessage;
    try {
      message = JSON.parse(raw) as DataMessage;
    } catch {
      return;
    }
    this.handleDataMessageValue(session, message);
  }

  private handleDataMessageValue(session: PeerSession, message: DataMessage) {
    if (message.type === 'requestSnapshot') {
      if (session.role === 'publisher') this.sendSnapshot(session);
      return;
    }
    if (
      message.type !== 'snapshotChunk'
      || !Number.isInteger(message.index)
      || !Number.isInteger(message.total)
      || message.total < 1
      || message.total > MAX_CHUNKS
      || message.index < 0
      || message.index >= message.total
      || typeof message.payload !== 'string'
    ) return;
    const transfer = session.transfers.get(message.transferId) ?? {
      total: message.total,
      chunks: new Array<string>(message.total),
    };
    if (transfer.total !== message.total) return;
    transfer.chunks[message.index] = message.payload;
    session.transfers.set(message.transferId, transfer);
    if (transfer.chunks.filter((chunk) => chunk !== undefined).length !== transfer.total) return;
    session.transfers.delete(message.transferId);
    try {
      const snapshot = parseSharedFlowSnapshot(transfer.chunks.join(''));
      if ((this.remoteFlow?.revision ?? -1) >= snapshot.revision) return;
      this.remoteFlow = snapshot;
      this.emit();
    } catch (error) {
      this.failViewer(error);
    }
  }

  private sendSnapshot(session: PeerSession) {
    if (!this.localFlow) return;
    if (!session.fallbackActive && session.channel?.readyState !== 'open') return;
    const serialized = JSON.stringify(this.localFlow);
    const transferId = crypto.randomUUID();
    const total = Math.ceil(serialized.length / CHUNK_SIZE);
    if (total > MAX_CHUNKS) return;
    for (let index = 0; index < total; index += 1) {
      const message = {
        type: 'snapshotChunk',
        transferId,
        index,
        total,
        payload: serialized.slice(index * CHUNK_SIZE, (index + 1) * CHUNK_SIZE),
      } satisfies DataMessage;
      if (session.fallbackActive) this.sendSignal(session, message);
      else session.channel?.send(JSON.stringify(message));
    }
  }

  private activateFallback(session: PeerSession) {
    if (!this.sessions.has(session.sessionId) || session.fallbackActive) return;
    session.fallbackActive = true;
    session.peer.onconnectionstatechange = null;
    if (session.channel) session.channel.onclose = null;
    session.channel?.close();
    session.peer.close();
    try {
      this.sendSignal(session, { type: 'requestSnapshot' });
      this.connectionState = 'connected';
      this.lastError = null;
      this.emit();
    } catch (error) {
      this.failViewer(error);
    }
  }

  private sendSignal(session: PeerSession, signal: UserFlowSignal) {
    if (!this.directory.sendUserFlowSignal(session.targetConnectionId, session.sessionId, signal)) {
      throw new Error('シグナリングサーバーへ接続できません');
    }
  }

  private async flushPendingIce(session: PeerSession) {
    const pending = session.pendingIce.splice(0);
    for (const candidate of pending) await session.peer.addIceCandidate(candidate);
  }

  private closeViewerSession(notifyRemote: boolean) {
    for (const session of this.sessions.values()) {
      if (session.role === 'viewer') this.closeSession(session, notifyRemote);
    }
  }

  private closeSession(session: PeerSession, notifyRemote: boolean) {
    this.sessions.delete(session.sessionId);
    if (notifyRemote) {
      this.directory.sendUserFlowSignal(session.targetConnectionId, session.sessionId, { type: 'close' });
    }
    session.channel?.close();
    session.peer.close();
  }

  private failViewer(error: unknown) {
    this.directory.setUserFlowViewing(null);
    this.connectionState = 'failed';
    this.lastError = error instanceof Error ? error.message : String(error);
    this.emit();
  }

  private emit() {
    this.listeners.forEach((listener) => listener());
  }
}